import { useCallback, useEffect, useRef, useState } from 'react';
import { FreeFocusInside } from 'react-focus-lock';
import cx from 'classnames';
import { anyItemHasFocus, isCustomSection, MenuListDesign } from '.';
import { BrowserName, getBrowserName } from '../../../browser-name';
import { useDesignTokens } from '../../../theme';
import { restoreTextSelection, saveTextSelection } from '../../../util';
import { getScrollbarSize } from '../../../util/dom';
import { setRef } from '../../../util/ref-util';
import { useKeyboardShortcuts } from '../../KeyboardShortcuts/hooks/useKeyboardShortcuts';
import { Popover } from '../../Popover';
import './MenuPopover.css';
import cn from './MenuPopover.css.json';
import { jsx as _jsx } from "react/jsx-runtime";
export const MenuPopover = props => {
  const popoverRef = useRef(null);
  const contentRef = useRef(null);
  const {
    getActionsByKeyEvent
  } = useKeyboardShortcuts();
  const [isOpening, setIsOpening] = useState(false);
  const [scrollbarOffset, setScrollbarOffset] = useState();
  const {
    isOpen,
    showFocus,
    onDismiss,
    focusLocation,
    isSubMenu,
    enableContextMenuEvent,
    placement,
    referenceElementRef,
    animation,
    onItemSelected,
    popoverTheme,
    isBusy
  } = props;

  // We need a short timeout after opening during which we will not respond
  // to outside click events.  This is because we do not want to respond to
  // the event that opened the Menu.
  useEffect(() => {
    if (isOpen) {
      setIsOpening(true);
      const timeoutId = setTimeout(() => setIsOpening(false), 10);
      return () => clearTimeout(timeoutId);
    }
  }, [isOpen]);

  // If the menu is open and none of the items have keyboard focus, we should focus
  // the entire menu to ensure that we get keyboard events
  const contentAlreadyFocused = contentRef.current && document.activeElement && contentRef.current === document.activeElement;
  useEffect(() => {
    if (!isOpen) return; // If the menu is closed, it should not get focus (and the contentRef should be null soon)
    if (isSubMenu) return; // Only the primary menu should take focus
    if (showFocus && anyItemHasFocus(focusLocation)) return; // Focus should be on the item
    if (contentAlreadyFocused) return; // Already done

    saveTextSelection(true);
    contentRef.current?.focus();
    restoreTextSelection();
  }, [isOpen, isSubMenu, contentAlreadyFocused, focusLocation, showFocus]);
  useEffect(() => {
    // No section to display (all items were deleted), close the menu and restore focus
    if (isOpen && !isBusy && props.sections.length === 0) {
      onDismiss?.(true);
    }
  }, [isOpen, isBusy, props.sections, onDismiss]);
  const onKeyUp = useCallback(event => {
    if (focusLocation?.length && contentRef.current && document.activeElement && contentRef.current === document.activeElement && (event.key === 'Enter' || event.key === ' ')) {
      event.preventDefault();
      let sections = props.sections;
      for (let i = 0; i < focusLocation.length; i++) {
        const section = sections[focusLocation[i][0]];
        if (!section || isCustomSection(section)) return;
        const item = section.items[focusLocation[i][1]];
        if (item.disabled) return;
        if (item.subSections) sections = item.subSections;else onItemSelected(focusLocation, item.key);
      }
    }
  }, [focusLocation, onItemSelected, props.sections]);
  useEffect(() => {
    if (isOpen && getBrowserName() === BrowserName.Firefox) setScrollbarOffset(`${getScrollbarSize()}px`);
  }, [isOpen]);

  // if consumers use useOnOutsideMouseDown function, because useOnOutsideMouseDown also listen mousedown event,
  // when useOnOutsideMouseDown cause reRender, because the onDismiss function object passed in is different everytime,
  // this will cause the mousedown listener here to be unbound and rebound and not fire.
  // NOVACOMMONS-15970
  const onDismissRef = useRef();
  onDismissRef.current = onDismiss;
  // Dismiss the menu if the user clicks on anything that is not part of a menu
  const onCloseMenu = useCallback(event => {
    const target = event.target;
    if (!isOpen || isOpening || !target || !onDismissRef.current) return;
    if (event instanceof MouseEvent || event instanceof WheelEvent) {
      const allMenuContent = document.getElementsByClassName(cn.menuPopover);
      for (const content of allMenuContent) {
        if (content && content.contains(target)) {
          return;
        }
      }
    }
    onDismissRef.current();
  }, [isOpening, isOpen]);
  useEffect(() => {
    // Note: we need to use mousedown rather than click because the Menu
    // event fires immediately after mousedown.  If we used click, the isOpening
    // timeout would fail in case of a long click.
    window.addEventListener('mousedown', onCloseMenu);

    // Note: we need to listen resize action to close the menu.
    window.addEventListener('resize', onCloseMenu);

    // Note: we need to listen wheel action to close the menu.
    window.addEventListener('wheel', onCloseMenu);
    // this line comes from a basic scroll hook called useDisableScroll.
    // The relevant stackoverflow(https://stackoverflow.com/questions/4770025/how-to-disable-scrolling-temporarily)
    // giving the possible cause of the above line is that 'This code will work on Chrome 56 and further'.
    document.addEventListener('wheel', onCloseMenu);
    return () => {
      window.removeEventListener('mousedown', onCloseMenu);
      window.removeEventListener('resize', onCloseMenu);
      window.removeEventListener('wheel', onCloseMenu);
      document.removeEventListener('wheel', onCloseMenu);
    };
  }, [onCloseMenu]);
  const onContextMenu = e => {
    if (!enableContextMenuEvent) {
      e.stopPropagation();
      if (!e.ctrlKey || e.button !== 2) {
        e.preventDefault();
      }
    }
  };
  const onKeyDown = event => {
    // NOVACOMMONS-4055. Close menu for landmark
    const {
      actions
    } = getActionsByKeyEvent(event);
    if (actions.includes('openLandmarks')) {
      onDismiss?.();
    }
  };
  const onMousedown = e => {
    const target = e.target;
    // dispatch onSubmenuClosed on vertical scroll bar mouse down only.
    if (target && popoverRef.current === target) props.onSubmenuClosed?.(props.menuLevel);
  };
  const onScroll = () => {
    props.onSubmenuClosed?.(props.menuLevel);
  };
  const onFocus = () => {
    // when dragging scrollbar, the container will get focus, and any keyboard operations will be happened in container's onKeyDown listener.
    // It is not expected, so pass the focus to menu and let menu handle normal keyboard operations.
    contentRef.current?.focus();
  };
  const popoverAnimation = {
    closed: {
      opacity: 0,
      x: isSubMenu ? -8 : 0,
      y: isSubMenu ? 0 : -8
    },
    open: {
      opacity: 1,
      x: 0,
      y: 0,
      transition: {
        type: 'tween',
        duration: 0.3,
        ease: [0, 0.5, 0.2, 1]
      }
    },
    exit: {
      opacity: 0,
      x: isSubMenu ? -8 : 0,
      y: isSubMenu ? 0 : -8,
      transition: {
        type: 'tween',
        duration: 0.3,
        ease: [0.5, 0, 0.8, 1]
      }
    }
  };
  const dt = useDesignTokens();
  return /*#__PURE__*/_jsx(Popover, {
    futureTheme: popoverTheme,
    isOpen: isOpen,
    className: cx(cn.menuPopover, isOpen ? cn.opening : cn.closing),
    style: {
      maxWidth: props.maxWidth ?? '25rem',
      maxHeight: props.maxHeight ?? '75vh',
      padding: '0',
      '--popover-border-radius': '0.25rem',
      '--menu-scrollbar-offset': scrollbarOffset,
      '--menu-anim-dur-enter': dt.tokens['duration-300'],
      '--menu-anim-tf-enter': dt.tokens['ease-out'],
      '--menu-anim-dur-exit': dt.tokens['duration-300'],
      '--menu-anim-tf-exit': dt.tokens['ease-in']
    },
    hideArrow: true,
    allowOnContextMenuPropagation: true,
    focusLockProps: {
      disabled: true
    },
    referenceElementRef: referenceElementRef,
    ref: ref => {
      if (ref) {
        ref?.container?.addEventListener('contextmenu', onContextMenu);
        // NOVACOMMONS-4055
        ref?.container?.addEventListener('keydown', onKeyDown);
        // NOVACOMMONS-12320
        ref?.container?.addEventListener('mousedown', onMousedown);
        ref?.container?.addEventListener('scroll', onScroll);
        ref?.container?.addEventListener('focus', onFocus);
      } else {
        popoverRef.current?.removeEventListener('contextmenu', onContextMenu);
        popoverRef.current?.removeEventListener('keydown', onKeyDown);
        popoverRef.current?.removeEventListener('mousedown', onMousedown);
        popoverRef.current?.removeEventListener('scroll', onScroll);
        popoverRef.current?.removeEventListener('focus', onFocus);
      }
      setRef(popoverRef, ref?.container);
      if (!isOpening && getBrowserName() === BrowserName.Firefox && ref?.container && contentRef.current) {
        // for async context menu, closable menu, its sections will be set more than once.
        // if there is no scrollbar, popover paddingInlineEnd should be removed, otherwise should be added.
        if (ref.container.offsetHeight >= ref.container.scrollHeight) {
          ref.container.style.paddingInlineEnd = '0px';
          if (
          // why use ref?.container?.offsetWidth not ref?.container?.clientWidth here?
          // because in firefox, if there is a scroll bar, the width of the contentRef.current
          // need to be extra plus the width of the scroll bar.
          ref.container.offsetWidth < contentRef.current.offsetWidth) {
            setScrollbarOffset(undefined);
          }
        } else if (ref.container.style.paddingInlineEnd === '0px') {
          const scrollbarOffset = getScrollbarSize();
          ref.container.style.paddingInlineEnd = `${scrollbarOffset}px`;
          setScrollbarOffset(`${scrollbarOffset}px`);
        }
      }
    },
    placement: placement,
    position: props.position,
    animation: {
      variants: popoverAnimation,
      ...animation
    },
    testId: props.testId,
    children: /*#__PURE__*/_jsx(FreeFocusInside, {
      className: cn.menuPopoverInner,
      children: /*#__PURE__*/_jsx(MenuListDesign, {
        ref: contentRef,
        sections: props.sections,
        focusLocation: focusLocation,
        disabled: props.disabled,
        onKeyDown: props.onKeyDown,
        onKeyUp: onKeyUp,
        onItemSelected: onItemSelected,
        onItemClosed: props.onItemClosed,
        onItemHovered: props.onItemHovered,
        onItemLeaved: props.onItemLeaved,
        showFocus: showFocus,
        tokens: props.tokens,
        onSubmenuClosed: props.onSubmenuClosed,
        menuLevel: props.menuLevel,
        listTheme: props.listTheme,
        theme: props.theme,
        popoverTheme: popoverTheme,
        isBusy: isBusy
      })
    })
  });
};
//# sourceMappingURL=MenuPopover.js.map